/**
 * Domivo: намерение при посочване на страничната рейка.
 *
 * Проблемът, който решава: чистият CSS :hover гаси панела в мига, в който
 * мишката излезе от реда. Но пътят от реда до панела минава диагонално през
 * съседните редове - затова панелът "изчезва много бързо" и на практика е
 * неизползваем. Praktiker и eMAG го решават със забавяне, не с по-голям панел.
 *
 * Тук: два таймера.
 *   - затваряне със ЗАБАВЯНЕ - мишката има време да стигне до панела;
 *   - превключване към друга категория също със забавяне - минаването през
 *     съседен ред на път към панела вече не отваря чужд панел.
 *
 * CSS-ът пази :hover като резерва, така че при паднал JS менюто пак работи -
 * просто по-грубо.
 */
(() => {
  const CLOSE_DELAY = 260;   // мс, докато мишката пътува към панела
  const SWITCH_DELAY = 130;  // мс, преди да признаем нов ред за избран

  class DomivoRail extends HTMLElement {
    connectedCallback() {
      // Само там, където изобщо има посочване. На тъч рейката се скролва,
      // а панелите се отварят от самите връзки.
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      this.items = Array.from(this.querySelectorAll('.dmv-rail__item'));
      if (!this.items.length) return;

      this.openItem = null;
      this.closeTimer = 0;
      this.switchTimer = 0;
      this.setAttribute('data-enhanced', '');

      this.items.forEach((item) => {
        if (!item.querySelector('.dmv-rail__flyout')) return;
        item.addEventListener('pointerenter', () => this.request(item));
        item.addEventListener('pointerleave', () => this.scheduleClose());
      });

      this.addEventListener('pointerleave', () => this.scheduleClose());
      this.addEventListener('focusin', (e) => {
        const item = e.target.closest('.dmv-rail__item');
        if (item) this.open(item);
      });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !this.openItem) return;
        const link = this.openItem.querySelector('.dmv-rail__link');
        this.closeNow();
        if (link) link.focus();
      });
    }

    /** Иска отваряне - веднага ако нищо не е отворено, иначе със забавяне. */
    request(item) {
      clearTimeout(this.closeTimer);
      clearTimeout(this.switchTimer);

      if (!this.openItem) {
        this.open(item);
        return;
      }
      if (this.openItem === item) return;

      // Друг панел е отворен: изчакваме, преди да го сменим. Ако мишката
      // само е минала оттук на път надясно, до изтичането ще е излязла.
      this.switchTimer = setTimeout(() => {
        if (item.matches(':hover')) this.open(item);
      }, SWITCH_DELAY);
    }

    open(item) {
      clearTimeout(this.closeTimer);
      if (this.openItem && this.openItem !== item) {
        this.openItem.classList.remove('is-open');
      }
      item.classList.add('is-open');
      this.openItem = item;
    }

    scheduleClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        // Ако мишката вече е вътре в панела, не затваряме - панелът е
        // част от елемента, така че :hover още важи.
        if (this.openItem && this.openItem.matches(':hover')) return;
        this.closeNow();
      }, CLOSE_DELAY);
    }

    closeNow() {
      clearTimeout(this.switchTimer);
      if (this.openItem) this.openItem.classList.remove('is-open');
      this.openItem = null;
    }
  }

  /**
   * Бутонът "Всички категории" в хедъра - отваря същия списък като падащ.
   *
   * Клик, а не само hover: на тъч устройства hover няма, а и с клавиатура
   * бутонът трябва да се управлява. Hover-ът е добавка за мишка, със същото
   * забавяне при затваряне, за да не изчезва по пътя надолу.
   */
  class DomivoCatMenu extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('.dmv-catmenu__btn');
      this.panel = this.querySelector('.dmv-rail');
      if (!this.btn || !this.panel) return;

      this.timer = 0;
      this.btn.addEventListener('click', () => this.toggle());

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.addEventListener('pointerenter', () => {
          clearTimeout(this.timer);
          this.setOpen(true);
        });
        this.addEventListener('pointerleave', () => {
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.setOpen(false), CLOSE_DELAY);
        });
      }

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.setOpen(false);
          this.btn.focus();
        }
      });
      // Клик встрани затваря - иначе панелът остава да виси след докосване.
      document.addEventListener('click', (e) => {
        if (this.isOpen() && !this.contains(e.target)) this.setOpen(false);
      });
    }

    isOpen() {
      return this.hasAttribute('open');
    }

    toggle() {
      this.setOpen(!this.isOpen());
    }

    setOpen(on) {
      this.toggleAttribute('open', on);
      this.btn.setAttribute('aria-expanded', on ? 'true' : 'false');
    }
  }

  if (!customElements.get('domivo-rail')) {
    customElements.define('domivo-rail', DomivoRail);
  }
  if (!customElements.get('domivo-cat-menu')) {
    customElements.define('domivo-cat-menu', DomivoCatMenu);
  }
})();
